import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import { tableParams, type TablePage, type TableRequest } from './settingsCustomers'

/**
 * Settings -> Customer Services, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Customer_services.php`
 * si din cele trei capete ale lui `settings/CustomersV2.php` pe care ecranul
 * le chema:
 *
 *   servicesData               -> GET  /settings/customer_services/services
 *   addServiceModal            -> GET  /settings/customer_services/services/new
 *   customersV2::newService +
 *   update                     -> POST /settings/customer_services/services
 *   edit                       -> GET  /settings/customer_services/services/{id}
 *   changeStatus               -> POST /settings/customer_services/services/{id}/status
 *   customersV2::serviceTemplateAutocomplete
 *                              -> GET  /settings/customer_services/templates
 *   customersV2::getDeviceSubCategories
 *                              -> GET  /settings/customer_services/device_types/{id}/subtypes
 *   get_properties_json        -> GET  /settings/customer_services/properties
 *   service_properties         -> GET  /settings/customer_services/properties/options
 *   storeProperty/updateProperty -> POST /settings/customer_services/properties
 *   deleteProperty             -> POST /settings/customer_services/properties/{id}/remove
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 */

export type ServicesTab = {
  id: string
  label: string
  icon: string
  right: string
  href: string
}

export type CustomerServicesShell = {
  pageTitle: string
  moduleUrl: string
  tabs: ServicesTab[]
  ready: boolean
  currencies: string[]
  canCreate: boolean
  canEdit: boolean
  canCreateProperty: boolean
  canEditProperty: boolean
}

export const TABS = ['services', 'service_properties'] as const

export type ServicesTabId = (typeof TABS)[number]

export function isServicesTab(value: string): value is ServicesTabId {
  return (TABS as readonly string[]).includes(value)
}

/** Un rand din lista de servicii (`ServicesModel` din services.js). */
export type ServiceRow = {
  service_id: number
  service_name: string
  service_category: string
  priority_name: string
  coverage_name: string
  sla_name: string
  intervention_sla_name: string
  resolve_sla_name: string
  UM_name: string
  quantity: string
  cost: string
  currency: string
  description: string
  service_status: number
  DT_RowId?: string
}

/**
 * Un sablon de serviciu, asa cum il arata lista care se umple pe masura ce
 * scrii. Portalul trimitea aici un tabel HTML gata facut (`subtext`); aici sunt
 * campuri, si tabelul mic il deseneaza ecranul.
 */
export type ServiceTemplate = {
  id: number
  name: string
  category: string
  priority: string
  coverage: string
  measure: string
  responseSla: string
  interventionSla: string
  resolveSla: string
  hasQuantity: number
  hasCategory: number
  categoryType: string
}

export type DeviceType = { id: number; name: string; cost: string; currency: string }

export type DeviceSubtype = { id: number; name: string }

export type ServiceForm = {
  service: {
    id: number
    templateId: number
    templateName: string
    category: number
    subcategory: number
    quantity: string
    cost: string
    currency: string
    description: string
    hasQuantity: number
    hasCategory: number
    categoryType: string
    status: number
  }
  deviceTypes: DeviceType[]
  deviceSubtypes: DeviceSubtype[]
  template: ServiceTemplate
}

export type PropertyRow = {
  id: number
  name: string
  coverage: string
  response_sla: string
  intervention_sla: string
  resolve_sla: string
  coverage_id: number
  response_sla_id: number
  intervention_sla_id: number
  resolve_sla_id: number
  user_fullname: string
  created_at: string
  DT_RowId?: string
}

export type NamedOption = { id: number; name: string }

export type PropertyOptions = {
  coverages: NamedOption[]
  responseSla: NamedOption[]
  interventionSla: NamedOption[]
  resolveSla: NamedOption[]
}

const KEY = ['settings', 'customer-services'] as const

export const customerServicesShellQuery = queryOptions({
  queryKey: [...KEY, 'shell'],
  queryFn: () => apiGet<CustomerServicesShell>('/settings/customer_services'),
})

export function servicesQuery(r: TableRequest) {
  return queryOptions({
    queryKey: [...KEY, 'services', r],
    queryFn: () =>
      apiGet<TablePage<ServiceRow> & { ready: boolean }>(
        `/settings/customer_services/services?${tableParams(r).toString()}`,
      ),
  })
}

export const newServiceQuery = queryOptions({
  queryKey: [...KEY, 'service-form'],
  queryFn: () => apiGet<{ deviceTypes: DeviceType[]; companyId: number }>('/settings/customer_services/services/new'),
})

export function serviceQuery(serviceId: number) {
  return queryOptions({
    queryKey: [...KEY, 'service', serviceId],
    queryFn: () => apiGet<ServiceForm>(`/settings/customer_services/services/${serviceId}`),
    enabled: serviceId > 0,
  })
}

export function templatesQuery(search: string) {
  return queryOptions({
    queryKey: [...KEY, 'templates', search],
    queryFn: () =>
      apiGet<{ templates: ServiceTemplate[] }>(
        `/settings/customer_services/templates?q=${encodeURIComponent(search)}`,
      ),
  })
}

export function deviceSubtypesQuery(typeId: number) {
  return queryOptions({
    queryKey: [...KEY, 'subtypes', typeId],
    queryFn: () =>
      apiGet<{ deviceSubtypes: DeviceSubtype[] }>(`/settings/customer_services/device_types/${typeId}/subtypes`),
    enabled: typeId > 0,
  })
}

/** Fara `id` creeaza (`newService`), cu `id` modifica (`update`). */
export function saveService(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/customer_services/services', fields)
}

/** Fara `status` rastoarna ce e - ce voia sa faca `changeStatus`. */
export function setServiceStatus(serviceId: number, status: number) {
  return apiPost<{ saved: boolean; status: number }>(`/settings/customer_services/services/${serviceId}/status`, {
    status,
  })
}

export function propertiesQuery(r: TableRequest) {
  return queryOptions({
    queryKey: [...KEY, 'properties', r],
    queryFn: () =>
      apiGet<TablePage<PropertyRow>>(`/settings/customer_services/properties?${tableParams(r).toString()}`),
  })
}

export const propertyOptionsQuery = queryOptions({
  queryKey: [...KEY, 'property-options'],
  queryFn: () => apiGet<PropertyOptions>('/settings/customer_services/properties/options'),
})

export function saveProperty(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/customer_services/properties', fields)
}

export function removeProperty(propertyId: number) {
  return apiPost<{ saved: boolean }>(`/settings/customer_services/properties/${propertyId}/remove`, {})
}

export type { TablePage, TableRequest }
